import { useState, useRef, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';

const FUNCTION_URL = 'https://stellular-clafoutis-10452c.netlify.app/.netlify/functions/viajes-chat';
const WA_NUMBER = '5541920004775';

const WELCOME = {
  role: 'assistant',
  content: '✈️ Hola, soy el asistente de viajes de Click Travel Agency. ¿Desde qué ciudad vas a viajar?'
};

function formatMessage(text, onWaClick) {
  let safe = text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
  safe = safe.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
  safe = safe.replace(/^##\s?(.+)$/gm, '<strong style="display:block;margin-top:8px;font-size:15px;">$1</strong>');
  safe = safe.replace(/^#\s?(.+)$/gm, '<strong style="display:block;margin-top:8px;font-size:16px;">$1</strong>');
  safe = safe.replace(
    /((https?:\/\/|www\.|wa\.me\/)[^\s<&]+)/g,
    (match) => {
      const cleanMatch = match.replace(/[.,;:!?)\]]+$/, '');
      const trailing = match.slice(cleanMatch.length);
      const isWhatsApp = cleanMatch.includes('wa.me') || cleanMatch.includes(WA_NUMBER);
      if (isWhatsApp) {
        return `<a href="javascript:void(0)" onclick="window.__waClick && window.__waClick()" style="color:#FF8A00;text-decoration:underline;cursor:pointer;">${cleanMatch}</a>${trailing}`;
      }
      const href = cleanMatch.startsWith('http') ? cleanMatch : `https://${cleanMatch}`;
      return `<a href="${href}" target="_blank" rel="noopener noreferrer" style="color:#FF8A00;text-decoration:underline;">${cleanMatch}</a>${trailing}`;
    }
  );
  safe = safe.replace(/\n/g, '<br/>');
  return safe;
}

export default function ViajesChat({ onBack }) {
  const { user } = useAuth();
  const [messages, setMessages] = useState([WELCOME]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [loadingWa, setLoadingWa] = useState(false);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, loading]);

  useEffect(() => {
    const chatId = user?.uid;
    if (!chatId) { setLoadingHistory(false); return; }
    const loadHistory = async () => {
      try {
        const res = await fetch(FUNCTION_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ chatId, historyOnly: true }),
        });
        const data = await res.json();
        if (data.history && data.history.length > 0) {
          setMessages([WELCOME, ...data.history]);
        }
      } catch (err) {}
      finally {
        setLoadingHistory(false);
        setTimeout(() => scrollToBottom(), 100);
      }
    };
    loadHistory();
  }, [user?.uid]);

  const handleWhatsApp = async () => {
    const chatId = user?.uid;
    if (loadingWa) return;
    setLoadingWa(true);
    try {
      let summaryText = null;
      if (chatId && messages.filter(m => m.role === 'user').length > 0) {
        const res = await fetch(FUNCTION_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ chatId, summaryOnly: true }),
        });
        const data = await res.json();
        if (data.summary) summaryText = data.summary;
      }
      const text = summaryText || '✈️ Consulta desde el Asistente de Viajes de Click Travel Agency.';
      const url = `https://api.whatsapp.com/send?phone=${WA_NUMBER}&text=${encodeURIComponent(text)}`;
      window.open(url, '_blank');
    } catch (err) {
      const url = `https://api.whatsapp.com/send?phone=${WA_NUMBER}`;
      window.open(url, '_blank');
    } finally {
      setLoadingWa(false);
    }
  };

  useEffect(() => {
    window.__waClick = handleWhatsApp;
    return () => { delete window.__waClick; };
  }, [messages, user?.uid]);

  const sendMessage = async () => {
    const text = input.trim();
    if (!text || loading) return;
    const chatId = user?.uid;
    if (!chatId) {
      setMessages(prev => [...prev, { role: 'assistant', content: 'No pudimos identificar tu sesión. Por favor cierra sesión y vuelve a entrar.' }]);
      return;
    }
    setMessages(prev => [...prev, { role: 'user', content: text }]);
    setInput('');
    setLoading(true);
    try {
      const res = await fetch(FUNCTION_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ chatId, message: text }),
      });
      const data = await res.json();
      if (data.reply) {
        setMessages(prev => [...prev, { role: 'assistant', content: data.reply }]);
      } else {
        setMessages(prev => [...prev, { role: 'assistant', content: 'Hubo un problema al procesar tu mensaje. Intenta de nuevo.' }]);
      }
    } catch (err) {
      setMessages(prev => [...prev, { role: 'assistant', content: 'No se pudo conectar. Revisa tu internet e intenta de nuevo.' }]);
    } finally { setLoading(false); }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 64px)', background: 'var(--bg)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '16px', borderBottom: '1px solid var(--navy-border)', background: 'var(--bg)' }}>
        <button onClick={onBack} style={{ background: 'none', border: 'none', color: 'var(--white)', fontSize: 20, cursor: 'pointer' }}>←</button>
        <div style={{ flex: 1 }}>
          <div style={{ color: 'var(--white)', fontWeight: 800, fontSize: 16 }}>✈️ Asistente de Viajes</div>
          <div style={{ color: 'var(--muted)', fontSize: 12 }}>Vuelos · Rutas · Precios</div>
        </div>
        <button onClick={handleWhatsApp} disabled={loadingWa}
          style={{ background: '#25D366', color: 'white', border: 'none', borderRadius: 20, padding: '6px 12px', fontSize: 12, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap', opacity: loadingWa ? 0.6 : 1 }}>
          {loadingWa ? '...' : '💬 WhatsApp'}
        </button>
      </div>
      <div style={{ flex: 1, overflowY: 'auto', padding: '16px', display: 'flex', flexDirection: 'column', gap: 10 }}>
        {loadingHistory && (
          <div style={{ alignSelf: 'center', color: 'var(--muted)', fontSize: 13 }}>Cargando conversación...</div>
        )}
        {!loadingHistory && messages.map((m, i) => (
          <div key={i}
            style={{ alignSelf: m.role === 'user' ? 'flex-end' : 'flex-start', maxWidth: '85%', background: m.role === 'user' ? 'var(--orange)' : 'var(--navy-card)', color: 'var(--white)', padding: '10px 14px', borderRadius: 14, fontSize: 14, lineHeight: 1.5 }}
            dangerouslySetInnerHTML={{ __html: formatMessage(m.content, handleWhatsApp) }}
          />
        ))}
        {loading && (
          <div style={{ alignSelf: 'flex-start', color: 'var(--muted)', fontSize: 13, padding: '0 14px' }}>Escribiendo...</div>
        )}
        <div ref={messagesEndRef} />
      </div>
      <div style={{ display: 'flex', gap: 8, padding: '12px 16px', borderTop: '1px solid var(--navy-border)', background: 'var(--bg)' }}>
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Escribe tu pregunta..."
          style={{ flex: 1, padding: '12px 14px', borderRadius: 20, border: '1px solid var(--navy-border)', background: 'var(--navy-card)', color: 'var(--white)', fontSize: 14, outline: 'none' }}
        />
        <button
          onClick={sendMessage}
          disabled={loading || !input.trim()}
          style={{ background: 'var(--orange)', color: 'var(--white)', border: 'none', borderRadius: '50%', width: 44, height: 44, fontSize: 18, cursor: 'pointer', opacity: (loading || !input.trim()) ? 0.5 : 1 }}
        >➤</button>
      </div>
    </div>
  );
                   }
